// Repositories page (RFC-0009 Task 1.5, §2/§6).
//
// Reads the live, filterable, paginated repository list from `GET /api/projects`
// (RFC-0006) via `useProjects` (ADR-0007), replacing the former static list. The filter
// rail (status + health toggles, PRD-0001 1.2.2) and name-search (`?q=`, 1.2.3)
// are forwarded as query params AND reflected in the URL so a filtered view is
// shareable/back-navigable. Renders the §6 states: skeleton, envelope-aware
// error panel, and two distinct empty cases — no repos at all (connect CTA) vs.
// active filters matching nothing (clear-filters).

import { useSearchParams } from 'react-router-dom';
import {
  GithubLogo,
  Plus,
  MagnifyingGlass,
  X,
  CaretLeft,
  CaretRight,
} from '@phosphor-icons/react';
import type { RepositoryStatus } from '@portal/shared-types';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { Button } from '@/components/ui/button';
import { CardGridSkeleton } from '@/components/ui/query-skeletons';
import { ErrorPanel } from '@/components/ui/error-panel';
import { ProjectCard } from '@/components/projects/project-card';
import { useProjects, type ProjectListParams } from '@/lib/queries/projects';
import { useConnectRepo } from '@/context/connect-repo';

const PER_PAGE = 12;

const STATUS_OPTIONS: { value: RepositoryStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'experimental', label: 'Experimental' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'archived', label: 'Archived' },
];

const HEALTH_TOGGLES: { param: 'manifest_broken' | 'sync_failed' | 'stale'; label: string }[] = [
  { param: 'manifest_broken', label: 'Manifest broken' },
  { param: 'sync_failed', label: 'Sync failed' },
  { param: 'stale', label: 'Stale' },
];

export function RepositoriesPage() {
  const { open } = useConnectRepo();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get('q') ?? '';
  const status = (searchParams.get('status') as RepositoryStatus | null) ?? undefined;
  const manifestBroken = searchParams.get('manifest_broken') === 'true' ? true : undefined;
  const syncFailed = searchParams.get('sync_failed') === 'true' ? true : undefined;
  const stale = searchParams.get('stale') === 'true' ? true : undefined;
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const hasActiveFilters =
    q !== '' || Boolean(status) || Boolean(manifestBroken) || Boolean(syncFailed) || Boolean(stale);

  const params: ProjectListParams = {
    q: q || undefined,
    status,
    manifestBroken,
    syncFailed,
    stale,
    page,
    perPage: PER_PAGE,
  };
  const { data, isLoading, isError, error, refetch } = useProjects(params);

  const repos = data?.data ?? [];
  const pagination = data?.pagination;

  // Set/clear a single query param, resetting page (a new filter starts at p.1).
  function setParam(key: string, value: string | undefined) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  }

  function toggleHealth(param: string) {
    setParam(param, searchParams.get(param) === 'true' ? undefined : 'true');
  }

  function clearFilters() {
    setSearchParams(new URLSearchParams());
  }

  return (
    <>
      <PageHeader
        title="Repositories"
        description="Repos that publish docs to the portal via a .nmblr.portal.yaml manifest."
        action={
          <Button size="md" className="min-h-[44px]" onClick={() => open()}>
            <Plus size={15} weight="bold" />
            Connect repository
          </Button>
        }
      />

      {/* Filter rail: name-search + status + health toggles. */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlass
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            type="search"
            aria-label="Search repositories"
            placeholder="Search repositories…"
            value={q}
            onChange={(e) => setParam('q', e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-border bg-canvas py-2.5 pl-9 pr-3 text-[13px] text-text outline-none focus:border-accent"
          />
        </div>

        <select
          aria-label="Filter by status"
          value={status ?? ''}
          onChange={(e) => setParam('status', e.target.value || undefined)}
          className="min-h-[44px] rounded-lg border border-border bg-canvas px-3 py-2 text-[13px] text-text outline-none focus:border-accent"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {HEALTH_TOGGLES.map((t) => {
          const active = searchParams.get(t.param) === 'true';
          return (
            <button
              key={t.param}
              type="button"
              aria-pressed={active}
              onClick={() => toggleHealth(t.param)}
              className={[
                'inline-flex min-h-[44px] items-center rounded-full border px-3.5 py-2 text-xs font-medium transition-colors',
                active
                  ? 'border-accent bg-accent-faint text-text'
                  : 'border-border text-muted hover:text-text',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}

        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-muted hover:text-text"
          >
            <X size={13} weight="bold" />
            Clear filters
          </button>
        )}
      </div>

      {isLoading ? (
        <CardGridSkeleton count={PER_PAGE} />
      ) : isError ? (
        <ErrorPanel error={error} onRetry={() => void refetch()} />
      ) : repos.length === 0 ? (
        hasActiveFilters ? (
          <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
            <EmptyState
              icon={<MagnifyingGlass size={22} />}
              title="No repositories match your filters"
              description="Try a different search term or clear the active filters to see all connected repositories."
              action={
                <Button size="md" variant="secondary" className="min-h-[44px]" onClick={clearFilters}>
                  <X size={15} weight="bold" />
                  Clear filters
                </Button>
              }
            />
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
            <EmptyState
              icon={<GithubLogo size={22} />}
              title="No repositories connected yet"
              description="Add a .nmblr.portal.yaml manifest to a repo, then connect it here. The sync worker will ingest its docs on the next push."
              action={
                <Button size="md" className="min-h-[44px]" onClick={() => open()}>
                  <GithubLogo size={16} />
                  Connect a repository
                </Button>
              }
            />
          </div>
        )
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {repos.map((repo) => (
              <ProjectCard key={repo.id} project={repo} />
            ))}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <p className="text-xs text-muted">
                Page {pagination.page} of {pagination.totalPages} · {pagination.total}{' '}
                {pagination.total === 1 ? 'repository' : 'repositories'}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  className="min-h-[44px]"
                  disabled={pagination.page <= 1}
                  onClick={() => setParam('page', String(pagination.page - 1))}
                >
                  <CaretLeft size={14} weight="bold" />
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="min-h-[44px]"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setParam('page', String(pagination.page + 1))}
                >
                  Next
                  <CaretRight size={14} weight="bold" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
